import { z } from 'zod'
import { furnitureClassSchema } from '../enums/furniture-class.enum.js'
import { primitiveSchema } from './primitive.dto.js'
import { eventDetailDtoSchema } from './event.dto.js'

/** `item` = one catalog piece; `group` = a table-and-chair arrangement. */
const IMPORT_CANDIDATE_KINDS = ['item', 'group'] as const
export const importCandidateKindSchema = z.enum(IMPORT_CANDIDATE_KINDS)
export type ImportCandidateKind = z.infer<typeof importCandidateKindSchema>

/** How the importer will resolve one unique catalog candidate from the SVG. */
export const importCandidateActionSchema = z.enum(['create', 'match', 'skip'])

/**
 * One unique furniture (or group) type discovered in a legacy SVG, with a
 * suggested match against the tenant catalog when a `legacyImportKey` hits.
 */
export const eventImportCandidateDtoSchema = z.object({
  /** Stable key used for matching / storage (`legacyImportKey`). */
  legacyKey: z.string(),
  /** `item` = single catalog piece; `group` = table+chair arrangement template. */
  kind: importCandidateKindSchema,
  /** Suggested display name when creating. */
  suggestedName: z.string(),
  class: furnitureClassSchema.optional(),
  /** How many of this key the plan needs (placements or member instances). */
  neededCount: z.number().int().nonnegative(),
  /** Current venue inventory for the matched item (0 if unmatched / group). */
  currentInventory: z.number().int().nonnegative(),
  /** Inventory after apply if this candidate is created or matched (items only). */
  inventoryAfter: z.number().int().nonnegative(),
  /** Auto-match when a catalog item/group already has this `legacyImportKey`. */
  matchedItemId: z.string().uuid().nullable(),
  matchedItemName: z.string().nullable(),
  matchedGroupId: z.string().uuid().nullable(),
  matchedGroupName: z.string().nullable(),
  /** Default action the UI should pre-select. */
  suggestedAction: importCandidateActionSchema,
  /** Shape preview for create (items only). */
  primitives: z.array(primitiveSchema).optional(),
  /** Human-readable size / layout hint (e.g. `95" × 40" · 10 chairs`). */
  detail: z.string().optional(),
})

export type EventImportCandidateDto = z.infer<typeof eventImportCandidateDtoSchema>

/** Dry-run result of parsing a legacy SVG against an event space/phase. */
export const eventImportPreviewDtoSchema = z.object({
  venueSpaceId: z.string().uuid(),
  phaseId: z.string().uuid(),
  phaseLabel: z.string(),
  spaceName: z.string(),
  spaceWidth: z.number(),
  spaceHeight: z.number(),
  /** Canvas size this SVG would produce (same pipeline as space floor-plan import). */
  suggestedWidth: z.number(),
  suggestedHeight: z.number(),
  /**
   * True when the venue space canvas size does not match this SVG. Re-import the
   * floor plan on the space before applying furniture.
   */
  spaceSizeMismatch: z.boolean(),
  furniturePlacements: z.number().int().nonnegative(),
  notePlacements: z.number().int().nonnegative(),
  candidates: z.array(eventImportCandidateDtoSchema),
})

export type EventImportPreviewDto = z.infer<typeof eventImportPreviewDtoSchema>

/** Result of applying an import: updated event detail + a short summary. */
export const eventImportApplyResultDtoSchema = z.object({
  event: eventDetailDtoSchema,
  /** The plan the import wrote, so an open editing room can be brought up to date. */
  phaseId: z.string().uuid(),
  summary: z.object({
    furniturePlaced: z.number().int().nonnegative(),
    notesPlaced: z.number().int().nonnegative(),
    itemsCreated: z.number().int().nonnegative(),
    groupsCreated: z.number().int().nonnegative(),
    inventoryTopUps: z.number().int().nonnegative(),
  }),
})

export type EventImportApplyResultDto = z.infer<typeof eventImportApplyResultDtoSchema>
